import React, { Component } from 'react';
import moment from 'moment';
import Constants from '../utils/Constants';

export const HISTORY_DATE_PRESETS = [
  { key: 'all', labelKey: 'HISTORY_DATE_ALL' },
  { key: 'today', labelKey: 'HISTORY_DATE_TODAY' },
  { key: 'yesterday', labelKey: 'HISTORY_DATE_YESTERDAY' },
  { key: 'this_week', labelKey: 'HISTORY_DATE_THIS_WEEK' },
  { key: 'this_month', labelKey: 'HISTORY_DATE_THIS_MONTH' },
];

export const ANALYTICS_DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
];

export const buildHistoryDateParams = (datePreset, fromDate, toDate) => {
  const preset = String(datePreset || 'all').toLowerCase();
  if (preset === 'custom') {
    const from = String(fromDate || '').trim();
    const to = String(toDate || fromDate || '').trim();
    if (from && to) {
      return { datePreset: 'custom', fromDate: from, toDate: to };
    }
    return {};
  }
  if (preset && preset !== 'all') {
    return { datePreset: preset };
  }
  return {};
};

export const formatCustomChipLabel = (fromDate, toDate) => {
  const from = String(fromDate || '').trim();
  const to = String(toDate || from || '').trim();
  if (from && to && from === to) {
    return moment(from, 'YYYY-MM-DD').format('DD MMM YYYY');
  }
  if (from && to) {
    return `${moment(from, 'YYYY-MM-DD').format('DD MMM')} – ${moment(to, 'YYYY-MM-DD').format('DD MMM YYYY')}`;
  }
  return Constants.HISTORY_DATE_CUSTOM || 'Custom';
};

/**
 * Shared history / analytics date chips with Custom (single day or range).
 *
 * Props:
 * - datePreset, fromDate, toDate
 * - onChange({ datePreset, fromDate, toDate })
 * - filters: array of { key, labelKey? | label? }
 * - includeCustom (default true)
 * - label
 */
class HistoryDateFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showCustom: false,
      customMode: 'single',
      draftFrom: '',
      draftTo: '',
      error: '',
    };
  }

  openCustom = () => {
    const today = moment().format('YYYY-MM-DD');
    const fromDate = this.props.fromDate || today;
    const toDate = this.props.toDate || this.props.fromDate || today;
    const isRange = !!(this.props.fromDate && this.props.toDate && this.props.fromDate !== this.props.toDate);
    this.setState({
      showCustom: true,
      customMode: isRange ? 'range' : 'single',
      draftFrom: fromDate,
      draftTo: isRange ? toDate : fromDate,
      error: '',
    });
  };

  emit = (next) => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(next);
    }
  };

  onChipClick = (key) => {
    if (key === 'custom') {
      this.openCustom();
      return;
    }
    this.emit({ datePreset: key, fromDate: null, toDate: null });
  };

  applyCustom = () => {
    const { customMode, draftFrom, draftTo } = this.state;
    const from = String(draftFrom || '').trim();
    const toRaw = customMode === 'single' ? from : String(draftTo || '').trim();
    if (!moment(from, 'YYYY-MM-DD', true).isValid() || !moment(toRaw, 'YYYY-MM-DD', true).isValid()) {
      this.setState({ error: Constants.HISTORY_DATE_INVALID || 'Enter a valid date' });
      return;
    }
    let start = from;
    let end = toRaw;
    if (moment(end).isBefore(moment(start))) {
      start = toRaw;
      end = from;
    }
    const span = moment(end).diff(moment(start), 'days') + 1;
    if (span > 90) {
      this.setState({ error: Constants.HISTORY_DATE_RANGE_MAX || 'Max 90 days' });
      return;
    }
    if (moment(end).isAfter(moment(), 'day')) {
      this.setState({ error: Constants.HISTORY_DATE_FUTURE_NOT_ALLOWED || 'Future dates not allowed' });
      return;
    }
    this.setState({ showCustom: false, error: '' });
    this.emit({ datePreset: 'custom', fromDate: start, toDate: end });
  };

  render() {
    const {
      datePreset = 'all',
      fromDate = null,
      toDate = null,
      filters = HISTORY_DATE_PRESETS,
      includeCustom = true,
      label = Constants.HISTORY_DATE_FILTER,
      className = '',
    } = this.props;
    const { showCustom, customMode, draftFrom, draftTo, error } = this.state;
    const today = moment().format('YYYY-MM-DD');
    const chipFilters = [
      ...filters,
      ...(includeCustom ? [{ key: 'custom', label: datePreset === 'custom' ? formatCustomChipLabel(fromDate, toDate) : (Constants.HISTORY_DATE_CUSTOM || 'Custom') }] : []),
    ];

    return (
      <div className={`history-date-filter ${className}`.trim()}>
        <div className="history-date-filter__label">{label}</div>
        <div className="history-date-filter__chips">
          {chipFilters.map((filter) => {
            const chipLabel = filter.label || Constants[filter.labelKey] || filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                className={`history-date-chip${datePreset === filter.key ? ' is-active' : ''}`}
                onClick={() => this.onChipClick(filter.key)}
              >
                {chipLabel}
              </button>
            );
          })}
        </div>

        {showCustom ? (
          <div className="history-custom-date">
            <div className="history-custom-date__modes">
              <button
                type="button"
                className={`history-date-chip${customMode === 'single' ? ' is-active' : ''}`}
                onClick={() => this.setState({ customMode: 'single', draftTo: draftFrom, error: '' })}
              >
                {Constants.HISTORY_DATE_SINGLE_DAY || 'Single day'}
              </button>
              <button
                type="button"
                className={`history-date-chip${customMode === 'range' ? ' is-active' : ''}`}
                onClick={() => this.setState({ customMode: 'range', error: '' })}
              >
                {Constants.HISTORY_DATE_MULTI_DAY || 'Multiple days'}
              </button>
            </div>
            <div className="history-custom-date__fields">
              <div className="history-custom-date__field">
                <label>{customMode === 'single' ? (Constants.HISTORY_DATE_DAY || 'Date') : (Constants.HISTORY_DATE_FROM || 'From')}</label>
                <input
                  type="date"
                  className="form-control"
                  max={today}
                  value={draftFrom}
                  onChange={(e) => {
                    const value = e.target.value;
                    this.setState({
                      draftFrom: value,
                      draftTo: customMode === 'single' ? value : draftTo,
                      error: '',
                    });
                  }}
                />
              </div>
              {customMode === 'range' ? (
                <div className="history-custom-date__field">
                  <label>{Constants.HISTORY_DATE_TO || 'To'}</label>
                  <input
                    type="date"
                    className="form-control"
                    min={draftFrom || undefined}
                    max={today}
                    value={draftTo}
                    onChange={(e) => this.setState({ draftTo: e.target.value, error: '' })}
                  />
                </div>
              ) : null}
            </div>
            {error ? <div className="history-custom-date__error">{error}</div> : null}
            <div className="history-custom-date__actions">
              <button type="button" className="btn btn-light" onClick={() => this.setState({ showCustom: false, error: '' })}>
                Cancel
              </button>
              <button type="button" className="btn add-item" onClick={this.applyCustom}>
                Apply
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default HistoryDateFilter;
